import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getPlayers, updatePlayer, getScenarios, createScenario, updateScenario, getScheduleDay } from '../../api';
import { TARGETS } from '../GPSData';
import { SCENARIO_DAYS } from '../../scenarioDays';
import { gymFocusFor } from '../player/PlayerDataContext';
import { downloadBlob, renderReportPdf } from '../../pdfExport';
import { useUrlParamArray } from '../../hooks/useUrlParam';

// Future Targets — every player's forward-looking day-by-day plan, all at once: which GPS load
// targets apply each day of their current/upcoming scenario period(s), and which gym session (if
// any) they have that day. The per-player "percentage" control reuses the scenarios table's
// existing player_id + intensity mechanism (a scenario assignment already supports being scoped
// to one player instead of the whole team, per GPSUpload.js's own "Applies to" picker) rather
// than adding a new column — a player-specific row for the same scenario+start_date as the team
// row simply overrides intensity for that player, exactly like GPSData.js's own target-scaling
// logic already resolves it (mirrored below, not reinvented).
//
// MD-day scaling is NOT governed by intensity — it's governed by md_minutes (expected minutes
// played that match), same distinction GPSData.js's `enriched` useMemo makes. The intensity %
// control here only moves non-MD (training) day targets.
//
// The per-player note reuses players.notes — the same field Players.js's profile editor already
// reads/writes — rather than a new page-scoped column, so a note left here shows up on the
// player's profile too (and vice versa). Flagged in case a plan-scoped note (tied to one
// scenario period, not the player generally) turns out to be what's actually wanted instead.

const METRICS = ['TD', 'RD', 'HID', 'SD', 'SA', 'Expl', 'HMLD'];

function addDaysStr(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}
function scaleTargets(raw, factor) {
  if (!raw) return null;
  if (factor === 1) return raw;
  return Object.fromEntries(
    Object.entries(raw).map(([k, { upper, lower }]) => [k, { upper: Math.round(upper * factor), lower: Math.round(lower * factor) }])
  );
}
function gymLabel(gym) {
  if (!gym || !gym.assigned) return null;
  const parts = [];
  if (gym.upper) parts.push('Upper');
  if (gym.lower) parts.push('Lower');
  if (gym.other) parts.push('Other');
  return parts.length ? parts.join(' + ') : 'Gym';
}

// Effective scenario periods for one player — dedupe by scenario+startDate; a player-specific row
// for that same key overrides the team-wide row (matches GPSData.js's own assignment-lookup
// precedence). Only current/upcoming periods, not fully-past ones. Pure function so it can be run
// once per player without re-deriving the dedupe logic inline.
function computePeriods(scenarios, playerId) {
  const byKey = new Map();
  for (const sc of scenarios) {
    if (sc.playerId !== 'team' && String(sc.playerId) !== String(playerId)) continue;
    const key = `${sc.scenario}|${sc.startDate}`;
    const existing = byKey.get(key);
    if (!existing || (existing.playerId === 'team' && sc.playerId !== 'team')) byKey.set(key, sc);
  }
  const today = new Date().toISOString().slice(0, 10);
  return [...byKey.values()]
    .filter(sc => {
      const days = SCENARIO_DAYS[sc.scenario] || [];
      const endDate = addDaysStr(sc.startDate, Math.max(days.length - 1, 0));
      return endDate >= addDaysStr(today, -1);
    })
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
}

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', marginBottom: 18 };

function NotesBox({ player, onSaved }) {
  const [value, setValue] = useState(player.notes || '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updatePlayer(player.id, { ...player, notes: value });
      setDirty(false);
      onSaved(updated);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <textarea
        value={value}
        onChange={e => { setValue(e.target.value); setDirty(true); }}
        placeholder="Note about this player…"
        rows={2}
        style={{ width: '100%', resize: 'vertical', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12.5, fontFamily: 'inherit' }}
      />
      {dirty && (
        <div style={{ marginTop: 6 }}>
          <button onClick={save} disabled={saving}
            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save note'}
          </button>
        </div>
      )}
    </div>
  );
}

function ScenarioPeriodCard({ playerId, playerPosition, period, scheduleDays, editingKey, editValue, setEditValue, onStartEdit, onCancelEdit, onSaveIntensity, saving }) {
  const key = `${playerId}|${period.scenario}|${period.startDate}`;
  const days = SCENARIO_DAYS[period.scenario] || [];
  const isCustom = period.playerId !== 'team';
  const isEditing = editingKey === key;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Scenario {period.scenario}</span>
          <span style={{ fontSize: 11, color: 'var(--text-dimmed)', marginLeft: 8 }}>starts {fmtDate(period.startDate)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: isCustom ? 'rgba(59,130,246,0.15)' : 'var(--border)', color: isCustom ? '#60a5fa' : 'var(--text-muted)' }}>
            {isCustom ? `Custom: ${period.intensity ?? 100}%` : `Team default: ${period.intensity ?? 100}%`}
          </span>
          {isEditing ? (
            <>
              <input type="number" min={50} max={120} step={5} value={editValue}
                onChange={e => setEditValue(Number(e.target.value))}
                style={{ width: 56, padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 11 }} />
              <button onClick={() => onSaveIntensity(period)} disabled={saving}
                style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={onCancelEdit} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}>
                Cancel
              </button>
            </>
          ) : (
            <button onClick={() => onStartEdit(period)}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              Edit %
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
          <thead>
            <tr style={{ textAlign: 'right', color: 'var(--text-dimmed)', fontSize: 9.5, textTransform: 'uppercase' }}>
              <th style={{ textAlign: 'left', padding: '5px 7px' }}>Date</th>
              <th style={{ padding: '5px 7px' }}>Day</th>
              <th style={{ padding: '5px 7px' }}>Gym</th>
              {METRICS.map(m => <th key={m} style={{ padding: '5px 7px' }}>{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {days.map((mdLabel, i) => {
              const date = addDaysStr(period.startDate, i);
              const raw = playerPosition ? TARGETS[period.scenario]?.[mdLabel]?.[playerPosition] : null;
              const factor = mdLabel === 'MD' ? ((period.md_minutes ?? 90) / 90) * 1.1 : (period.intensity ?? 100) / 100;
              const scaled = scaleTargets(raw, factor);
              const dayOverride = playerPosition ? period.target_overrides?.[date]?.[playerPosition] : null;
              const targets = dayOverride || scaled;
              const sched = scheduleDays[date];
              const gym = sched ? gymFocusFor(sched, playerId) : null;
              const gLabel = gymLabel(gym);
              return (
                <tr key={date} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ textAlign: 'left', padding: '6px 7px', color: 'var(--text-primary)' }}>{fmtDate(date)}</td>
                  <td style={{ textAlign: 'right', padding: '6px 7px', color: 'var(--text-muted)', fontWeight: mdLabel === 'MD' ? 700 : 400 }}>{mdLabel}</td>
                  <td style={{ textAlign: 'right', padding: '6px 7px', color: gLabel ? '#34d399' : 'var(--text-dimmed)' }}>{gLabel || '—'}</td>
                  {METRICS.map(m => (
                    <td key={m} style={{ textAlign: 'right', padding: '6px 7px', color: 'var(--text-secondary)' }}>
                      {targets?.[m] ? `${targets[m].lower.toLocaleString()}–${targets[m].upper.toLocaleString()}` : '—'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Player filter — a dropdown of checkboxes with explicit "Select all" / "Clear all" bulk
// actions. Neither of this app's existing multi-select controls (components/MultiSelect.js,
// InMatchAnalysis.js's PlayerMultiSelect) offers a real select-all beyond "empty = all" — this
// one keeps that same empty-means-all convention for the closed/default state, but adds the two
// explicit actions on top so a coach can start from "everyone" and remove a few, or start from
// "nobody" and add a few, either way.
function PlayerFilter({ players, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    const h = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = id => onChange(selectedIds.includes(id) ? selectedIds.filter(v => v !== id) : [...selectedIds, id]);
  const selectAll = () => onChange(players.map(p => String(p.id)));
  const clearAll = () => onChange([]);

  const label = selectedIds.length === 0
    ? 'All players'
    : selectedIds.length === 1
      ? players.find(p => String(p.id) === selectedIds[0])?.name || '1 player'
      : `${selectedIds.length} players`;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px 7px 12px', borderRadius: 8,
        border: `1px solid ${selectedIds.length > 0 ? '#3b82f6' : 'var(--border-strong)'}`,
        background: selectedIds.length > 0 ? 'rgba(59,130,246,0.1)' : 'var(--bg-card)',
        color: selectedIds.length > 0 ? '#93c5fd' : 'var(--text-primary)',
        fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', minWidth: 180,
      }}>
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.15s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 10,
          minWidth: 240, maxHeight: 340, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
          <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
            <button onClick={selectAll} style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
              Select all
            </button>
            <button onClick={clearAll} style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
              Clear all
            </button>
          </div>
          {players.map(p => {
            const id = String(p.id);
            const checked = selectedIds.length === 0 || selectedIds.includes(id);
            return (
              <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer',
                fontSize: 12.5, color: 'var(--text-primary)',
                background: selectedIds.includes(id) ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(id)} style={{ accentColor: '#3b82f6', flexShrink: 0 }} />
                {p.name}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FutureTargets() {
  const [players, setPlayers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleDays, setScheduleDays] = useState({});
  const [editingKey, setEditingKey] = useState(null); // `${playerId}|${scenario}|${startDate}`
  const [editValue, setEditValue] = useState(100);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const contentRef = useRef(null);
  const [selectedIds, setSelectedIds] = useUrlParamArray('players'); // [] = everyone, matching this app's existing multi-select convention

  const loadScenarios = () =>
    getScenarios().then(scens => setScenarios((scens || []).map(s => ({ ...s, startDate: s.start_date?.slice(0, 10), playerId: s.player_id }))));

  useEffect(() => {
    Promise.all([getPlayers(), getScenarios()])
      .then(([pl, scens]) => {
        setPlayers((pl || []).filter(p => p.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setScenarios((scens || []).map(s => ({ ...s, startDate: s.start_date?.slice(0, 10), playerId: s.player_id })));
      })
      .finally(() => setLoading(false));
  }, []);

  const visiblePlayers = useMemo(
    () => (selectedIds.length === 0 ? players : players.filter(p => selectedIds.includes(String(p.id)))),
    [players, selectedIds]
  );

  // Scoped to visiblePlayers, not the full roster — narrowing the selection also cuts down the
  // schedule-day fetch below to just the dates that are actually needed.
  const periodsByPlayer = useMemo(() => {
    const map = new Map();
    for (const p of visiblePlayers) map.set(p.id, computePeriods(scenarios, p.id));
    return map;
  }, [visiblePlayers, scenarios]);

  const allDates = useMemo(() => {
    const set = new Set();
    for (const periods of periodsByPlayer.values()) {
      periods.forEach(p => (SCENARIO_DAYS[p.scenario] || []).forEach((_, i) => set.add(addDaysStr(p.startDate, i))));
    }
    return [...set];
  }, [periodsByPlayer]);

  useEffect(() => {
    if (!allDates.length) { setScheduleDays({}); return; }
    let cancelled = false;
    Promise.all(allDates.map(d => getScheduleDay(d).then(r => [d, r]).catch(() => [d, null])))
      .then(entries => { if (!cancelled) setScheduleDays(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [allDates]);

  const startEdit = (playerId, period) => { setEditingKey(`${playerId}|${period.scenario}|${period.startDate}`); setEditValue(period.intensity ?? 100); };

  const saveIntensity = async (playerId, period) => {
    setSaving(true);
    try {
      if (period.playerId !== 'team') {
        await updateScenario(period.id, {
          scenario: period.scenario, start_date: period.startDate, player_id: playerId,
          intensity: editValue, md_minutes: period.md_minutes ?? 90, target_overrides: period.target_overrides || {},
        });
      } else {
        await createScenario({
          scenario: period.scenario, start_date: period.startDate, player_id: playerId,
          intensity: editValue, md_minutes: 90, target_overrides: {},
        });
      }
      await loadScenarios();
      setEditingKey(null);
    } finally { setSaving(false); }
  };

  const handleNoteSaved = updatedPlayer => {
    setPlayers(prev => prev.map(p => (p.id === updatedPlayer.id ? { ...p, notes: updatedPlayer.notes } : p)));
  };

  // Same captureBlocks/packBlocksIntoPages/A4 pipeline as every other export in the app
  // (src/pdfExport.js) — each player's whole card (name, note, and all their period tables) is
  // marked as one data-report-block, so the packer keeps it together on one page rather than
  // cutting a table across a page boundary; a block only ever gets hard-sliced if a single
  // player's card is physically taller than one A4 page on its own (see pdfExport.js).
  const handleExport = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--bg-app').trim() || '#0f172a';
      const arrayBuffer = await renderReportPdf(contentRef.current, bgColor, { scale: 1.5, imageFormat: 'JPEG', imageQuality: 0.85 });
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const dateStr = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `future-targets-${dateStr}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Future Targets</h1>
          <p style={{ fontSize: 12, color: 'var(--text-dimmed)', margin: '4px 0 0' }}>
            Every player's day-by-day scenario plan — GPS targets and gym sessions, before they happen.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PlayerFilter players={players} selectedIds={selectedIds} onChange={setSelectedIds} />
          <button
            onClick={handleExport}
            disabled={exporting || !visiblePlayers.length}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 9,
              border: '1px solid var(--border-strong)',
              background: exporting ? 'var(--border)' : 'var(--bg-card)',
              color: exporting ? 'var(--text-dimmed)' : 'var(--text-secondary)',
              fontSize: 13, fontWeight: 600, cursor: exporting ? 'not-allowed' : 'pointer',
            }}
          >
            {exporting ? 'Exporting…' : 'PDF Export'}
          </button>
        </div>
      </div>

      <div ref={contentRef}>
      {visiblePlayers.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No players match the current selection.</div>
      )}
      {visiblePlayers.map(player => {
        const periods = periodsByPlayer.get(player.id) || [];
        return (
          <div key={player.id} data-report-block="true" style={card}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
              {player.name}
              {player.position && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-dimmed)', marginLeft: 8 }}>{player.position}</span>}
            </div>

            <NotesBox player={player} onSaved={handleNoteSaved} />

            {periods.length === 0 ? (
              <div style={{ color: 'var(--text-dimmed)', fontSize: 12, padding: '10px 0' }}>No current or upcoming scenario assigned.</div>
            ) : periods.map(period => (
              <ScenarioPeriodCard
                key={`${period.scenario}|${period.startDate}`}
                playerId={player.id}
                playerPosition={player.position}
                period={period}
                scheduleDays={scheduleDays}
                editingKey={editingKey}
                editValue={editValue}
                setEditValue={setEditValue}
                onStartEdit={p => startEdit(player.id, p)}
                onCancelEdit={() => setEditingKey(null)}
                onSaveIntensity={p => saveIntensity(player.id, p)}
                saving={saving}
              />
            ))}
          </div>
        );
      })}
      </div>
    </div>
  );
}
